/**
 * Frame pacing instrumentation: frame-time histogram over the last 5 s, tick
 * and render times, dropped-frame counter, and a small 2D canvas that draws
 * them. Turns red when a budget is exceeded.
 */

export const BUDGET_TICK_MS = 0.25;
export const BUDGET_FRAME_MS = 1.5;

export class PerfMeter {
  readonly frameTimes = new Float64Array(1024);
  private head = 0;
  private count = 0;
  tickMs = 0;
  tickMsMax = 0;
  renderMs = 0;
  renderMsMax = 0;
  frameMs = 0;
  droppedFrames = 0;
  droppedTicks = 0;
  /** Estimated display interval (ms), the median of recent frame deltas. */
  refreshMs = 16.7;
  private sorted = new Float64Array(1024);
  private lastEstimate = 0;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private frames = 0;
  private startTime = 0;

  constructor(parent: HTMLElement) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = 240;
    this.canvas.height = 64;
    this.canvas.className = "perf";
    parent.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d")!;
  }

  /** Record a frame delta (ms) at time `now`. */
  frame(deltaMs: number, now: number): void {
    if (this.startTime === 0) this.startTime = now;
    this.frameMs = deltaMs;
    this.frameTimes[this.head] = deltaMs;
    this.head = (this.head + 1) % this.frameTimes.length;
    if (this.count < this.frameTimes.length) this.count++;
    this.frames++;
    if (now - this.lastEstimate > 1000 && this.count > 30) {
      this.lastEstimate = now;
      this.sorted.set(this.frameTimes.subarray(0, this.count));
      const view = this.sorted.subarray(0, this.count);
      view.sort();
      this.refreshMs = view[Math.floor(this.count / 2)]!;
    }
    if (this.frames > 10 && deltaMs > this.refreshMs * 1.6) this.droppedFrames++;
  }

  tick(ms: number): void {
    this.tickMs = ms;
    if (ms > this.tickMsMax) this.tickMsMax = ms;
  }

  render(ms: number): void {
    this.renderMs = ms;
    if (ms > this.renderMsMax) this.renderMsMax = ms;
  }

  resetMax(): void {
    this.tickMsMax = 0;
    this.renderMsMax = 0;
    this.droppedFrames = 0;
    this.droppedTicks = 0;
  }

  /** Samples within the last `windowMs` given a frame rate estimate. */
  private recent(windowMs: number): number {
    return Math.min(this.count, Math.round(windowMs / Math.max(1, this.refreshMs)));
  }

  draw(): void {
    const ctx = this.ctx;
    const W = this.canvas.width;
    const H = this.canvas.height;
    const n = this.recent(5000);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, 0, W, H);
    const over = this.tickMs > BUDGET_TICK_MS || this.renderMs > BUDGET_FRAME_MS;
    const barW = W / Math.max(1, n);
    const scale = H / (this.refreshMs * 3);
    for (let i = 0; i < n; i++) {
      const idx = (this.head - n + i + this.frameTimes.length) % this.frameTimes.length;
      const v = this.frameTimes[idx]!;
      const hh = Math.min(H, v * scale);
      ctx.fillStyle = v > this.refreshMs * 1.6 ? "#e0453a" : over ? "#e0a030" : "#7fd08a";
      ctx.fillRect(i * barW, H - hh, Math.max(1, barW - 0.5), hh);
    }
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.beginPath();
    const y = H - this.refreshMs * scale;
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }

  summary(): string {
    return (
      `frame ${this.frameMs.toFixed(1)}ms (${(1000 / this.refreshMs).toFixed(0)} Hz)  ` +
      `tick ${(this.tickMs * 1000).toFixed(0)}us max ${(this.tickMsMax * 1000).toFixed(0)}us  ` +
      `render ${this.renderMs.toFixed(2)}ms max ${this.renderMsMax.toFixed(2)}ms  ` +
      `dropped ${this.droppedFrames}`
    );
  }
}
